import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { combineLatest, of, switchMap, tap } from 'rxjs';
import { examDayListFilter } from '../../../src/client/store/filterStore.js';
import { getExamDayStatistics } from '../api/statistics.js';
import { ILSStatistics } from '../db/statistics.js';

@customElement('exd-statistics')
export class ExdStatistics extends ChemistryLitElement {
  @state() private readonly _fetched = new RXController(this, false);
  @state() private readonly _filter = new RXController(this, examDayListFilter, filter$ =>
    filter$.pipe(tap(_ => this._fetched.next(false))),
  );

  @state() private readonly _statistics = new RXController(
    this,
    combineLatest([this._fetched.observable, this._filter.observable]),
    obs$ =>
      obs$.pipe(
        switchMap(([doFetch, filter]) =>
          doFetch ? getExamDayStatistics(filter)(selectionSet(() => ILSStatistics, 1)) : of(null),
        ),
      ),
  );

  private _statisticRender() {
    return [
      {
        key: '1',
        label: 'Prüfungsplätze angeboten',
        value: this._statistics.value?.availableExamSlots ?? '-',
      },
      {
        key: '2',
        label: 'Prüfungsplätze vergeben',
        value: this._statistics.value?.allocatedExamSlots ?? '-',
      },
      {
        key: '3',
        label: 'Erfolgreiche Buchungen',
        value: this._statistics.value?.bookings ?? '-',
      },
      {
        key: '4',
        label: 'Stornierungen kostenlos',
        value: this._statistics.value?.freeCancellations ?? '-',
      },
      {
        key: '5',
        label: 'Stornierungen 1/2 Preis',
        value: this._statistics.value?.halfPriceCancellations ?? '-',
      },
      {
        key: '6',
        label: 'Stornierungen Vollpreis',
        value: this._statistics.value?.lateCancellations ?? '-',
      },
      {
        key: '7',
        label: 'No-Shows',
        value: this._statistics.value?.noShows ?? '-',
      },
      {
        key: '8',
        label: 'Ø Zeit bis zum Ergebnis',
        value: this._statistics.value ? this._statistics.value.averageDaysToResult.toFixed(2) + ' Tage' : '-',
      },
      {
        key: '9',
        label: 'Durchfallquote',
        value: this._statistics.value ? `${(this._statistics.value.failureRate * 100).toFixed(2)}%` : '-',
      },
    ];
  }

  override render() {
    return html`
      <x-basic-card ${css({ padding: this.spacing.md, width: '100%', background: '#EFEFEF' })}>
        <x-toggle-content label="Statistiken" .content=${() => this._content()} ${css({ width: '100%' })}>
        </x-toggle-content>
      </x-basic-card>
    `;
  }

  private _content() {
    if (!this._fetched.value)
      return html`
        <x-flex center crossaxis-center>
          <x-button @click=${() => this._fetched.next(true)}> Berechne Statistiken </x-button>
        </x-flex>
      `;
    if (this._statistics.isLoading)
      return html`
        <x-flex center crossaxis-center space="md">
          <x-loader></x-loader>
          <x-text> Das Berechnen der Statistiken für große Zeitspannen kann bis zu einer Minute dauern </x-text>
        </x-flex>
      `;
    return html`
      <x-grid columns="1fr 1fr 1fr" slot="content" space="lg">
        ${repeat(
          this._statisticRender(),
          stat => stat.key,
          stat => {
            return html`
              <x-flex space="sm">
                <x-text>${stat.label}</x-text>
                <x-h2>${stat.value}</x-h2>
              </x-flex>
            `;
          },
        )}
      </x-grid>
    `;
  }
}
